import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Check, Clock, Ban, Loader2, ChevronRight, Users, ExternalLink } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { Button } from '../../components/ui/button';
import PageHelp from '../../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

const STAGE_META = {
  contacted:     { label: 'Interest Sent',  color: 'bg-amber-50 text-amber-700 border-amber-200',  icon: Mail },
  interest_sent: { label: 'Interest Sent',  color: 'bg-amber-50 text-amber-700 border-amber-200',  icon: Mail },
  accepted:      { label: 'Accepted',       color: 'bg-green-50 text-green-700 border-green-200',   icon: Check },
  unavailable:   { label: 'Unavailable',    color: 'bg-red-50 text-red-600 border-red-200',         icon: Ban },
  hired:         { label: 'Hired',          color: 'bg-yellow-50 text-yellow-800 border-yellow-200', icon: Check },
};

const StageBadge = ({ stage }) => {
  const meta = STAGE_META[stage] || { label: stage, color: 'bg-zinc-50 text-zinc-500 border-zinc-200', icon: Clock };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border ${meta.color}`}
      style={MONO}>
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {meta.label}
    </span>
  );
};

const OutreachLog = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND}/api/dashboard/outreach-log`);
      const d = await r.json();
      if (d.success) setItems(d.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="outreach-log-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight"
            style={SERIF} data-testid="outreach-log-heading">
            Outreach Log
          </h1>
          <p className="text-xs text-muted-foreground mt-1 uppercase tracking-[0.15em]" style={MONO}>
            Interest expressions sent via Flence
          </p>
        </div>
        <Button
          onClick={() => navigate('/dashboard/pipeline')}
          className="rounded-none bg-foreground text-background hover:bg-foreground/80 h-9 text-xs gap-1.5 flex-shrink-0"
          data-testid="go-to-pipeline-btn"
        >
          <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
          Pipeline
        </Button>
        <PageHelp pageKey="outreach" />
      </div>

      {/* Flow explainer */}
      <div className="bg-white border border-border px-5 py-4">
        <div className="flex items-center gap-6 text-xs text-muted-foreground flex-wrap">
          {[
            { icon: Mail, label: 'Interest Sent', note: 'Awaiting response' },
            { icon: Check, label: 'Accepted', note: 'Email revealed' },
            { icon: Ban, label: 'Unavailable', note: 'Candidate paused' },
          ].map(({ icon: Icon, label, note }) => (
            <div key={label} className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5 text-muted-foreground/50" strokeWidth={1.5} />
              <span style={MONO} className="uppercase tracking-widest">{label}</span>
              <span className="text-muted-foreground/40">— {note}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <div className="text-center py-24 bg-white border border-border" data-testid="outreach-log-empty">
          <Mail className="w-8 h-8 text-muted-foreground/25 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-semibold text-sm mb-1">No outreach yet</p>
          <p className="text-xs text-muted-foreground mb-5">
            Express interest in candidates from your Pipeline using the <Mail className="w-3 h-3 inline mb-0.5" /> button.
          </p>
          <Button onClick={() => navigate('/dashboard/pipeline')} variant="outline" className="rounded-none text-xs h-8">
            Go to Pipeline
          </Button>
        </div>
      )}

      {/* Outreach items */}
      {items.length > 0 && (
        <div className="space-y-2" data-testid="outreach-log-list">
          {items.map(item => {
            const c = item.candidate || {};
            const sentDate = item.outreach_sent_at || item.last_action_date;
            const formattedDate = sentDate
              ? new Date(sentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : '—';

            return (
              <div
                key={item.id}
                className="bg-white border border-border p-4 flex items-start gap-4 group"
                data-testid={`outreach-item-${item.id}`}
              >
                {/* Candidate photo */}
                <div
                  className="flex-shrink-0 overflow-hidden bg-secondary cursor-pointer"
                  style={{ width: 44, height: 56, filter: 'grayscale(0.5)', transition: 'filter 200ms' }}
                  onMouseEnter={e => (e.currentTarget.style.filter = 'grayscale(0)')}
                  onMouseLeave={e => (e.currentTarget.style.filter = 'grayscale(0.5)')}
                  onClick={() => c.id && navigate(`/candidate/${c.id}`)}
                >
                  {c.photo_url ? (
                    <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover"
                      onError={e => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-muted-foreground/50">
                        {c.full_name ? c.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        onClick={() => c.id && navigate(`/candidate/${c.id}`)}
                        className="font-semibold text-sm leading-snug hover:underline text-left truncate block"
                        style={SERIF}
                        data-testid={`outreach-candidate-name-${item.id}`}
                      >
                        {c.full_name || 'Unknown'}
                      </button>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[c.seniority, c.core_role].filter(Boolean).join(' ')}
                        {c.location_city && <span className="text-muted-foreground/50"> · {c.location_city}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StageBadge stage={item.stage} />
                    </div>
                  </div>

                  {/* Outreach message preview */}
                  {(item.outreach_message || item.notes) && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic pl-2 border-l border-border">
                      {item.outreach_message || item.notes}
                    </p>
                  )}

                  {/* Email disclosure */}
                  {item.email_disclosed && c.email && (
                    <div className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-green-50 border border-green-100 w-fit">
                      <Mail className="w-2.5 h-2.5 text-green-600" strokeWidth={1.5} />
                      <a href={`mailto:${c.email}`} className="text-xs text-green-700 hover:underline"
                        data-testid={`outreach-email-${item.id}`}>
                        {c.email}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground/50" style={MONO}>{formattedDate}</span>
                    {c.id && (
                      <button
                        onClick={() => navigate(`/candidate/${c.id}`)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                        style={MONO}
                        data-testid={`view-profile-${item.id}`}
                      >
                        View profile <ExternalLink className="w-2.5 h-2.5" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Chevron */}
                <ChevronRight
                  className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground flex-shrink-0 mt-1 cursor-pointer"
                  strokeWidth={1.5}
                  onClick={() => navigate('/dashboard/pipeline')}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      {items.length > 0 && (
        <p className="text-xs text-muted-foreground text-center py-2" style={MONO}>
          Manage candidates and advance stages from the{' '}
          <button onClick={() => navigate('/dashboard/pipeline')} className="underline hover:no-underline">Pipeline</button>
        </p>
      )}
    </div>
  );
};

export default OutreachLog;
